import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axios from "axios";
import BaseURL from "../../api/AppURL";

export default function RolePermissionAssign() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const handleSnackbarClose = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.get(
        `${BaseURL}/add/roles/permission`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      setRoles(res.data.roles || []);
      setPermissions(res.data.permissions || []);
      setGroups(res.data.permission_groups || []);
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionAllToggle = (checked) => {
    if (checked) {
      setSelectedPermissions(permissions.map((p) => p.id));
    } else {
      setSelectedPermissions([]);
    }
  };

  const handleGroupToggle = (groupName, checked) => {
    const groupPermissions = permissions
      .filter((p) => p.group_name === groupName)
      .map((p) => p.id);
    if (checked) {
      setSelectedPermissions((prev) => [
        ...new Set([...prev, ...groupPermissions]),
      ]);
    } else {
      setSelectedPermissions((prev) =>
        prev.filter((id) => !groupPermissions.includes(id))
      );
    }
  };

  const handlePermissionToggle = (permissionId, checked) => {
    if (checked) {
      setSelectedPermissions((prev) => [...prev, permissionId]);
    } else {
      setSelectedPermissions((prev) =>
        prev.filter((id) => id !== permissionId)
      );
    }
  };

  const handleSave = async () => {
    if (!selectedRole) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please select a role",
      });
      return;
    }
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(
        `${BaseURL}/role/permission/store`,
        {
          role_id: selectedRole.id,
          permission: selectedPermissions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      setSnackbar({
        open: true,
        severity: "success",
        message: res.data.message || "Permissions updated successfully",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to save permissions",
      });
    }
  };

  const isAllSelected =
    permissions.length > 0 &&
    selectedPermissions.length === permissions.length;

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Assign Permissions to Role
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Autocomplete
              options={roles}
              getOptionLabel={(option) => option.name}
              value={selectedRole}
              onChange={(_, newValue) => setSelectedRole(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Role" />
              )}
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={isAllSelected}
                  onChange={(e) =>
                    handlePermissionAllToggle(e.target.checked)
                  }
                />
              }
              label="Permission All"
            />

            <Divider sx={{ my: 2 }} />

            {groups.map((group) => {
              const groupPermissions = permissions.filter(
                (p) => p.group_name === group.group_name
              );
              const isGroupSelected =
                groupPermissions.length > 0 &&
                groupPermissions.every((p) =>
                  selectedPermissions.includes(p.id)
                );

              return (
                <Accordion key={group.group_name}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ bgcolor: "#f5f5f5" }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isGroupSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleGroupToggle(group.group_name, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      }
                      label={group.group_name}
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ pl: 2 }}>
                      {groupPermissions.map((perm) => (
                        <FormControlLabel
                          key={perm.id}
                          control={
                            <Checkbox
                              checked={selectedPermissions.includes(perm.id)}
                              onChange={(e) =>
                                handlePermissionToggle(
                                  perm.id,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={perm.name}
                        />
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              sx={{ mt: 2 }}
            >
              Save Changes
            </Button>
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={handleSnackbarClose}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
